import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {ConfigService, FeatureTogglesService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {filter, switchMap} from 'rxjs/operators';
import {DcpScriptInjection} from '../model/dcp-script-injection.model';
import {ScriptInjectionStatusService} from './script-injection-status.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ScriptInjectionService {

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private configService: ConfigService,
    private scriptInjectionStatusService: ScriptInjectionStatusService,
    private featureTogglesService: FeatureTogglesService
  ) {
  }

  /**
   * Injects scripts into the index.html if the application is opened from the browser context
   */
  injectExternalScripts() {
    if (isPlatformBrowser(this.platformId)) {

      this.configService.getArray('scripts').forEach(async (script: DcpScriptInjection) => {
        if (script.featureToggle)
        {
          this.featureTogglesService.isEnabled(script.featureToggle).pipe(
            filter(Boolean),
            switchMap(() => this.injectScript(script)),
            untilDestroyed(this)
          ).subscribe();
        } else {
          await this.injectScript(script);
        }
      });
    }
   }

  private async injectScript(script: DcpScriptInjection) {
    await this.injectExternalScript(script);
    this.scriptInjectionStatusService.setNewInjectionStatus(script.id);
  }

  /**
   * Creates the Script HTML dom element according to the given MetaData and appends it to the given target
   *
   * @param scriptInjection MetaData for the script to inject
   */
  injectExternalScript(scriptInjection: DcpScriptInjection): Promise<void> {
    return new Promise((resolve, reject) => {
      const element = document[scriptInjection.target] as HTMLDivElement;
      const script = this.createScriptElement(scriptInjection);

      script.addEventListener('load', () => resolve());
      script.addEventListener('error', e => reject(e.error));

      element.appendChild(script);
    });
  }

  /**
   * Creates the Script HTML dom element according to the given MetaData
   *
   * @param scriptInjection MetaData for the script element to create
   */
  createScriptElement(scriptInjection: DcpScriptInjection): HTMLScriptElement {
    const script = document.createElement('script');

    if (scriptInjection.url) {
      script.setAttribute('src', scriptInjection.url);
    }

    if (scriptInjection.attributes) {
      Object.keys(scriptInjection.attributes).forEach(attributeName => {
        script.setAttribute(attributeName, scriptInjection.attributes[attributeName]);
      });
    }

    script.id = scriptInjection.id;

    script.type = scriptInjection.type || '';
    script.innerHTML = scriptInjection.content || '';

    script.async = !!scriptInjection.async;
    script.defer = !!scriptInjection.defer;

    return script;
  }
}
